import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare, ShieldCheck, FileText, BarChart3, Send, CheckCircle2,
  AlertCircle, Key, RefreshCw, Smartphone, Copy, ExternalLink,
  HelpCircle, Check, Eye, Settings, Zap, QrCode, LogOut
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8080';

const WhatsAppDashboard = () => {
  const [activeTab, setActiveTab] = useState('qr_connect'); // 'qr_connect' | 'dispatcher' | 'logs' | 'config' | 'guide'
  const [workflowType, setWorkflowType] = useState('otp'); // 'otp' | 'invoice' | 'report' | 'custom'
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  // QR Web Session State (Zero Meta Setup)
  const [qrSession, setQrSession] = useState({
    status: 'DISCONNECTED',
    session_id: '',
    qr_code_url: '',
    connected_number: '911414473405',
    connected_name: 'Kalp Admin',
  });

  // Config State
  const [config, setConfig] = useState({
    waba_id: '',
    phone_number_id: '',
    masked_token: 'Not Set',
    api_version: 'v21.0',
    webhook_verify_token: 'kalp_wa_webhook_secret_key',
    default_country_code: '91',
    business_display_name: 'Kalp OS',
    is_ready: false,
  });

  // Config Edit Form State
  const [configForm, setConfigForm] = useState({
    phone_number_id: '',
    access_token: '',
    waba_id: '',
    webhook_verify_token: '',
    default_country_code: '91',
  });

  // Analytics & Logs State
  const [logsData, setLogsData] = useState({
    stats: {
      total_sent: 0,
      total_delivered: 0,
      total_read: 0,
      total_failed: 0,
      estimated_cost_inr: 0.0,
    },
    logs: [],
  });

  // Country code & Phone Input
  const [selectedCountry, setSelectedCountry] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('9876543210');

  // Dispatch Form States
  const [otpForm, setOtpForm] = useState({
    otp_code: '839201',
    template_name: 'kalp_auth_otp',
  });

  const [invoiceForm, setInvoiceForm] = useState({
    customer_name: 'Priya Sharma',
    order_id: 'INV-2026-089',
    amount_formatted: '₹14,999.00',
    invoice_date: '05 Mar 2026',
    pdf_url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
    invoice_filename: 'Invoice_INV-2026-089.pdf',
  });

  const [reportForm, setReportForm] = useState({
    merchant_name: 'Rajesh Mart',
    report_date: '05 March 2026',
    revenue_str: '₹48,950',
    orders_count: '34',
    visitors_count: '1,280',
    pending_shipments: '2',
    dashboard_url_path: 'dashboard',
  });

  const [customForm, setCustomForm] = useState({
    message: 'Hello! Thank you for contacting Kalp OS support. How can we assist your business today?',
  });

  const [dispatchResult, setDispatchResult] = useState(null);

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
    showNotification(`Copied to clipboard!`, 'success');
  };

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/whatsapp/config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setConfigForm({
          phone_number_id: data.phone_number_id || '',
          access_token: '',
          waba_id: data.waba_id || '',
          webhook_verify_token: data.webhook_verify_token || 'kalp_wa_webhook_secret_key',
          default_country_code: data.default_country_code || '91',
        });
      }
    } catch (e) {
      console.warn('Failed to load WhatsApp config:', e);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/whatsapp/logs?limit=50`);
      if (res.ok) {
        const data = await res.json();
        setLogsData(data);
      }
    } catch (e) {
      console.warn('Failed to load WhatsApp logs:', e);
    }
  }, []);

  const loadQrStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/whatsapp/qr/status`);
      if (res.ok) {
        const data = await res.json();
        setQrSession(data);
      }
    } catch (e) {
      console.warn('Failed to load QR status:', e);
    }
  }, []);

  const handleGenerateQr = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/whatsapp/qr/generate`, { method: 'POST' });
      const data = await res.json();
      setQrSession(data);
      showNotification('QR Code generated! Scan from your WhatsApp app.', 'success');
    } catch (e) {
      showNotification('Failed to generate QR Code', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPairing = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/whatsapp/qr/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: '911414473405', display_name: 'Kalp Admin' }),
      });
      const data = await res.json();
      setQrSession(data);
      showNotification('✓ WhatsApp Paired successfully with Kalp Admin (+91 141 4473405)!', 'success');
    } catch (e) {
      showNotification('Pairing failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectQr = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/whatsapp/qr/disconnect`, { method: 'POST' });
      const data = await res.json();
      setQrSession(data);
      showNotification('WhatsApp session disconnected', 'success');
    } catch (e) {
      showNotification('Failed to disconnect', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    loadLogs();
    loadQrStatus();
    const interval = setInterval(() => {
      loadLogs();
      loadQrStatus();
    }, 10000);
    return () => clearInterval(interval);
  }, [loadConfig, loadLogs, loadQrStatus]);

  // Handle Save Config
  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/whatsapp/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configForm),
      });
      const data = await res.json();
      if (res.ok) {
        showNotification('WhatsApp credentials saved successfully!', 'success');
        loadConfig();
      } else {
        showNotification(data.detail || 'Failed to save config', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Error saving credentials', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Full Phone Builder
  const getFullPhone = () => {
    const raw = phoneNumber.trim();
    if (raw.startsWith('+')) return raw;
    return `${selectedCountry}${raw}`;
  };

  // Dispatch Action
  const handleDispatch = async () => {
    setLoading(true);
    setDispatchResult(null);
    const fullPhone = getFullPhone();

    try {
      let endpoint = '';
      let body = {};

      if (workflowType === 'otp') {
        endpoint = '/api/v1/whatsapp/send-otp';
        body = {
          phone: fullPhone,
          otp_code: otpForm.otp_code,
          template_name: otpForm.template_name,
        };
      } else if (workflowType === 'invoice') {
        endpoint = '/api/v1/whatsapp/send-invoice';
        body = {
          phone: fullPhone,
          customer_name: invoiceForm.customer_name,
          order_id: invoiceForm.order_id,
          amount_formatted: invoiceForm.amount_formatted,
          invoice_date: invoiceForm.invoice_date,
          pdf_url: invoiceForm.pdf_url,
          invoice_filename: invoiceForm.invoice_filename,
        };
      } else if (workflowType === 'report') {
        endpoint = '/api/v1/whatsapp/send-daily-report';
        body = {
          phone: fullPhone,
          merchant_name: reportForm.merchant_name,
          report_date: reportForm.report_date,
          revenue_str: reportForm.revenue_str,
          orders_count: reportForm.orders_count,
          visitors_count: reportForm.visitors_count,
          pending_shipments: reportForm.pending_shipments,
          dashboard_url_path: reportForm.dashboard_url_path,
        };
      } else if (workflowType === 'custom') {
        endpoint = '/api/v1/whatsapp/send-custom';
        body = {
          phone: fullPhone,
          message: customForm.message,
        };
      }

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Dispatch failed');
      }

      setDispatchResult(data);
      showNotification(
        data.simulated
          ? `[Sandbox Mode] Simulated WhatsApp sent to ${data.recipient}`
          : `Live WhatsApp message dispatched to ${data.recipient}!`,
        'success'
      );
      loadLogs();
    } catch (err) {
      showNotification(err.message || 'Failed to dispatch WhatsApp message', 'error');
      setDispatchResult({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#090d16', color: '#f8fafc', padding: '24px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Toast Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              zIndex: 9999,
              background: notification.type === 'error' ? 'rgba(239, 68, 68, 0.95)' : 'rgba(16, 185, 129, 0.95)',
              color: '#ffffff',
              padding: '12px 20px',
              borderRadius: '10px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              backdropFilter: 'blur(10px)',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            {notification.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '24px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'linear-gradient(135deg, #25D366, #128C7E)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(37, 211, 102, 0.3)' }}>
              <MessageSquare size={26} color="#ffffff" />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: '700', margin: 0, letterSpacing: '-0.02em' }}>
                  WhatsApp Business Cloud API
                </h1>
                <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 8px', borderRadius: '20px', background: 'rgba(37, 211, 102, 0.15)', color: '#25D366', border: '1px solid rgba(37, 211, 102, 0.3)' }}>
                  Meta v21.0 Direct
                </span>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#94a3b8' }}>
                Zero-middleman direct Meta Cloud integration for Daily Reports, Tax Invoices & Login OTPs
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: config.is_ready ? '#22c55e' : '#eab308', display: 'inline-block', boxShadow: config.is_ready ? '0 0 10px #22c55e' : 'none' }}></span>
              <span style={{ fontSize: '12px', color: '#cbd5e1', fontWeight: 500 }}>
                {config.is_ready ? 'Connected to Meta API' : 'Testing / Sandbox Active'}
              </span>
            </div>
            <button
              onClick={() => { loadConfig(); loadLogs(); showNotification('Data refreshed', 'success'); }}
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#cbd5e1', padding: '8px 14px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        {/* Stats Strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', padding: '16px 20px' }}>
            <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, marginBottom: '6px' }}>Total Messages Dispatched</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#38bdf8' }}>{logsData.stats.total_sent}</div>
          </div>
          <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', padding: '16px 20px' }}>
            <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, marginBottom: '6px' }}>Delivered / Read</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#22c55e' }}>{logsData.stats.total_delivered + logsData.stats.total_read}</div>
          </div>
          <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', padding: '16px 20px' }}>
            <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, marginBottom: '6px' }}>Failed Delivery</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#f87171' }}>{logsData.stats.total_failed}</div>
          </div>
          <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', padding: '16px 20px' }}>
            <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, marginBottom: '6px' }}>Estimated Cost (Wholesale)</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#a855f7' }}>₹{logsData.stats.estimated_cost_inr.toFixed(2)}</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '24px', flexWrap: 'wrap' }}>
          {[
            { id: 'qr_connect', label: '⚡ Instant QR Scan (Zero Meta)', icon: <QrCode size={16} /> },
            { id: 'dispatcher', label: 'Live Simulator & Dispatch', icon: <Smartphone size={16} /> },
            { id: 'logs', label: `Delivery Logs (${logsData.logs.length})`, icon: <BarChart3 size={16} /> },
            { id: 'config', label: 'Meta API & Webhook Config', icon: <Settings size={16} /> },
            { id: 'guide', label: 'R&D Pricing & Rules Guide', icon: <HelpCircle size={16} /> },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 18px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #25D366' : '2px solid transparent',
                color: activeTab === tab.id ? '#25D366' : '#94a3b8',
                fontWeight: activeTab === tab.id ? 600 : 500,
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* TAB 0: INSTANT QR CONNECT (ZERO META SETUP) */}
        {activeTab === 'qr_connect' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(340px, 1fr) minmax(340px, 1.2fr)', gap: '24px' }}>
            {/* QR Scanner Card */}
            <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '28px', textAlign: 'center', position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                <QrCode size={22} color="#25D366" />
                <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>Instant QR Link</h2>
              </div>
              <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 20px' }}>
                Scan this QR code from your phone's WhatsApp to connect directly to <strong>Kalp Admin</strong> without Meta Developer setup!
              </p>

              {/* QR Display Area */}
              <div style={{ background: '#0b141a', padding: '20px', borderRadius: '16px', display: 'inline-block', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', marginBottom: '20px' }}>
                {qrSession.status === 'CONNECTED' ? (
                  <div style={{ width: '220px', height: '220px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(37, 211, 102, 0.1)', borderRadius: '12px', border: '2px solid #25D366' }}>
                    <CheckCircle2 size={56} color="#25D366" style={{ marginBottom: '12px' }} />
                    <div style={{ fontWeight: 700, fontSize: '16px', color: '#25D366' }}>Connected!</div>
                    <div style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '4px' }}>+{qrSession.connected_number}</div>
                  </div>
                ) : qrSession.qr_code_url ? (
                  <div style={{ position: 'relative' }}>
                    <img
                      src={qrSession.qr_code_url}
                      alt="WhatsApp Web Pairing QR"
                      style={{ width: '220px', height: '220px', borderRadius: '10px', display: 'block' }}
                    />
                    <div style={{ marginTop: '10px', fontSize: '11px', color: '#22c55e', fontWeight: 600 }}>
                      ⚡ Ready to Scan • Refreshing in real-time
                    </div>
                  </div>
                ) : (
                  <div style={{ width: '220px', height: '220px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
                    <QrCode size={48} color="#64748b" style={{ marginBottom: '12px', opacity: 0.5 }} />
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Click below to generate QR</div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {qrSession.status !== 'CONNECTED' ? (
                  <>
                    <button
                      onClick={handleGenerateQr}
                      disabled={loading}
                      style={{
                        background: 'linear-gradient(135deg, #25D366, #128C7E)',
                        color: '#ffffff',
                        fontWeight: 600,
                        fontSize: '14px',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '12px 24px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 15px rgba(37, 211, 102, 0.3)',
                      }}
                    >
                      <RefreshCw size={16} /> {qrSession.qr_code_url ? 'Regenerate QR' : 'Generate QR Code'}
                    </button>
                    {qrSession.qr_code_url && (
                      <button
                        onClick={handleConfirmPairing}
                        disabled={loading}
                        style={{
                          background: 'rgba(255,255,255,0.08)',
                          border: '1px solid rgba(255,255,255,0.15)',
                          color: '#f8fafc',
                          fontWeight: 600,
                          fontSize: '14px',
                          borderRadius: '10px',
                          padding: '12px 20px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Check size={16} color="#25D366" /> Confirm Pair (+91 141 4473405)
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={handleDisconnectQr}
                    disabled={loading}
                    style={{
                      background: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      color: '#f87171',
                      fontWeight: 600,
                      fontSize: '14px',
                      borderRadius: '10px',
                      padding: '12px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <LogOut size={16} /> Disconnect Session
                  </button>
                )}
              </div>
            </div>

            {/* How to Connect Instructions */}
            <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '28px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px', color: '#f8fafc' }}>
                📱 How to Connect in 30 Seconds:
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '13px', color: '#cbd5e1', lineHeight: '1.6' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#25D366', color: '#042f1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0, fontSize: '12px' }}>1</div>
                  <div>Open <strong>WhatsApp</strong> on your mobile phone or landline-linked phone.</div>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#25D366', color: '#042f1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0, fontSize: '12px' }}>2</div>
                  <div>Tap <strong>Menu (⋮)</strong> on Android or <strong>Settings</strong> on iPhone $\rightarrow$ select <strong>Linked Devices</strong>.</div>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#25D366', color: '#042f1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0, fontSize: '12px' }}>3</div>
                  <div>Tap <strong>"Link a Device"</strong> and point your camera at the QR code on the left.</div>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#22c55e', color: '#042f1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0, fontSize: '12px' }}>4</div>
                  <div>Once scanned, your <strong>Kalp Admin</strong> can immediately dispatch Invoices, Reports & OTPs!</div>
                </div>
              </div>

              {/* Status Banner */}
              <div style={{ marginTop: '24px', background: qrSession.status === 'CONNECTED' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(56, 189, 248, 0.08)', border: qrSession.status === 'CONNECTED' ? '1px solid rgba(34, 197, 94, 0.25)' : '1px solid rgba(56, 189, 248, 0.2)', borderRadius: '12px', padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: qrSession.status === 'CONNECTED' ? '#4ade80' : '#38bdf8', marginBottom: '4px', fontSize: '14px' }}>
                  <Zap size={16} /> Live Status: {qrSession.status === 'CONNECTED' ? 'ACTIVE & CONNECTED' : 'Awaiting QR Scan'}
                </div>
                <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                  {qrSession.status === 'CONNECTED'
                    ? `Paired with ${qrSession.connected_name} (+${qrSession.connected_number}). Messages dispatch live!`
                    : 'Once connected, go to "Live Simulator & Dispatch" tab to send messages to any number.'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 1: DISPATCHER & SIMULATOR */}
        {activeTab === 'dispatcher' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(350px, 1.2fr) minmax(320px, 0.8fr)', gap: '24px' }}>
            {/* Form Section */}
            <div style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '24px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {[
                  { id: 'otp', label: 'Case 3: Login OTP', icon: <ShieldCheck size={14} /> },
                  { id: 'invoice', label: 'Case 2: Invoice (PDF)', icon: <FileText size={14} /> },
                  { id: 'report', label: 'Case 1: Daily Report', icon: <BarChart3 size={14} /> },
                  { id: 'custom', label: 'Direct Message', icon: <MessageSquare size={14} /> },
                ].map((wf) => (
                  <button
                    key={wf.id}
                    onClick={() => setWorkflowType(wf.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      background: workflowType === wf.id ? '#25D366' : 'rgba(255,255,255,0.05)',
                      color: workflowType === wf.id ? '#042f1a' : '#cbd5e1',
                      border: workflowType === wf.id ? '1px solid #25D366' : '1px solid rgba(255,255,255,0.1)',
                      transition: 'all 0.2s',
                    }}
                  >
                    {wf.icon} {wf.label}
                  </button>
                ))}
              </div>

              {/* Recipient Phone Input (Universal) */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#cbd5e1', fontWeight: 500, marginBottom: '6px' }}>
                  Universal Recipient Phone Number
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    style={{
                      background: '#0f172a',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: '#ffffff',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                  >
                    <option value="+91">🇮🇳 +91 (India)</option>
                    <option value="+1">🇺🇸 +1 (USA/Canada)</option>
                    <option value="+44">🇬🇧 +44 (UK)</option>
                    <option value="+971">🇦🇪 +971 (UAE)</option>
                    <option value="+61">🇦🇺 +61 (Australia)</option>
                    <option value="+65">🇸🇬 +65 (Singapore)</option>
                    <option value="">Other (Custom)</option>
                  </select>
                  <input
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="9876543210 or full +919876543210"
                    style={{
                      flex: 1,
                      background: '#0f172a',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: '#ffffff',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                  />
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                  Normalized E.164: <code style={{ color: '#38bdf8' }}>{getFullPhone()}</code>
                </div>
              </div>

              {/* Dynamic Form per Workflow */}
              {workflowType === 'otp' && (
                <div>
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>6-Digit OTP Code</label>
                    <input
                      type="text"
                      maxLength="8"
                      value={otpForm.otp_code}
                      onChange={(e) => setOtpForm({ ...otpForm, otp_code: e.target.value })}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '10px 12px', fontSize: '15px', fontWeight: 600, letterSpacing: '2px' }}
                    />
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', background: 'rgba(37, 211, 102, 0.05)', border: '1px solid rgba(37, 211, 102, 0.15)', borderRadius: '8px', padding: '10px' }}>
                    ℹ️ Uses Meta's pre-approved Authentication category with native 1-tap <strong>"Copy Code"</strong> button.
                  </div>
                </div>
              )}

              {workflowType === 'invoice' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Customer Name</label>
                    <input
                      type="text"
                      value={invoiceForm.customer_name}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, customer_name: e.target.value })}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Order ID</label>
                    <input
                      type="text"
                      value={invoiceForm.order_id}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, order_id: e.target.value })}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Amount Formatted</label>
                    <input
                      type="text"
                      value={invoiceForm.amount_formatted}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, amount_formatted: e.target.value })}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Invoice Date</label>
                    <input
                      type="text"
                      value={invoiceForm.invoice_date}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                    />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>PDF Document URL (Public/Signed)</label>
                    <input
                      type="text"
                      value={invoiceForm.pdf_url}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, pdf_url: e.target.value })}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                    />
                  </div>
                </div>
              )}

              {workflowType === 'report' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Store / Merchant</label>
                    <input
                      type="text"
                      value={reportForm.merchant_name}
                      onChange={(e) => setReportForm({ ...reportForm, merchant_name: e.target.value })}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Report Date</label>
                    <input
                      type="text"
                      value={reportForm.report_date}
                      onChange={(e) => setReportForm({ ...reportForm, report_date: e.target.value })}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Daily Revenue</label>
                    <input
                      type="text"
                      value={reportForm.revenue_str}
                      onChange={(e) => setReportForm({ ...reportForm, revenue_str: e.target.value })}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Completed Orders</label>
                    <input
                      type="text"
                      value={reportForm.orders_count}
                      onChange={(e) => setReportForm({ ...reportForm, orders_count: e.target.value })}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Active Visitors</label>
                    <input
                      type="text"
                      value={reportForm.visitors_count}
                      onChange={(e) => setReportForm({ ...reportForm, visitors_count: e.target.value })}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Pending Shipments</label>
                    <input
                      type="text"
                      value={reportForm.pending_shipments}
                      onChange={(e) => setReportForm({ ...reportForm, pending_shipments: e.target.value })}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                    />
                  </div>
                </div>
              )}

              {workflowType === 'custom' && (
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Message Body (24-hr Service Window)</label>
                  <textarea
                    rows="4"
                    value={customForm.message}
                    onChange={(e) => setCustomForm({ ...customForm, message: e.target.value })}
                    style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', outline: 'none' }}
                  ></textarea>
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleDispatch}
                disabled={loading}
                style={{
                  width: '100%',
                  marginTop: '20px',
                  background: 'linear-gradient(135deg, #25D366, #128C7E)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '15px',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  boxShadow: '0 4px 15px rgba(37, 211, 102, 0.3)',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? <RefreshCw size={18} className="animate-spin" /> : <Send size={18} />}
                {loading ? 'Dispatching...' : `Send WhatsApp ${workflowType.toUpperCase()}`}
              </button>

              {dispatchResult && (
                <div style={{ marginTop: '16px', padding: '12px', borderRadius: '8px', background: dispatchResult.error ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)', border: dispatchResult.error ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid rgba(34, 197, 94, 0.2)', fontSize: '12px' }}>
                  <div style={{ fontWeight: 600, color: dispatchResult.error ? '#f87171' : '#4ade80', marginBottom: '4px' }}>
                    {dispatchResult.error ? 'Dispatch Error' : (dispatchResult.simulated ? '⚡ Dispatched (Sandbox Simulation)' : '✓ Dispatched via Meta API')}
                  </div>
                  {dispatchResult.recipient && <div style={{ color: '#cbd5e1' }}>Recipient: +{dispatchResult.recipient}</div>}
                  {dispatchResult.message_id && <div style={{ color: '#94a3b8', fontFamily: 'monospace' }}>ID: {dispatchResult.message_id}</div>}
                  {dispatchResult.error && <div style={{ color: '#fca5a5' }}>{dispatchResult.error}</div>}
                </div>
              )}
            </div>

            {/* Live WhatsApp Mobile Simulator */}
            <div>
              <div style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 500, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Eye size={14} /> Live WhatsApp Chat Preview
              </div>
              <div
                style={{
                  background: '#0b141a',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '24px',
                  padding: '16px',
                  minHeight: '420px',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
                  backgroundSize: '16px 16px',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Chat Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '12px', marginBottom: '16px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#00a884', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '14px', color: '#fff' }}>
                    K
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#e9edef', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {config.business_display_name || 'Kalp OS'}
                      <CheckCircle2 size={13} color="#00a884" />
                    </div>
                    <div style={{ fontSize: '11px', color: '#8696a0' }}>Official Business Account</div>
                  </div>
                </div>

                {/* WhatsApp Message Bubble */}
                <div
                  style={{
                    background: '#005c4b',
                    color: '#e9edef',
                    padding: '12px 14px',
                    borderRadius: '12px 12px 2px 12px',
                    maxWidth: '90%',
                    alignSelf: 'flex-start',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    fontSize: '13px',
                    lineHeight: '1.5',
                  }}
                >
                  {/* Case 3: OTP */}
                  {workflowType === 'otp' && (
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: 700, letterSpacing: '1px', marginBottom: '4px' }}>
                        *{otpForm.otp_code || '839201'}* is your Kalp OS verification code.
                      </div>
                      <div style={{ fontSize: '12px', color: '#c4d1d9', marginBottom: '10px' }}>
                        For your security, do not share this code with anyone. This code will expire in 10 minutes.
                      </div>
                      <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '8px', display: 'flex', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#53bdeb', fontWeight: 600, fontSize: '12px' }}>
                          <Copy size={13} /> Copy Code: {otpForm.otp_code}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Case 2: Invoice */}
                  {workflowType === 'invoice' && (
                    <div>
                      <div style={{ background: '#025143', borderRadius: '8px', padding: '10px', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <FileText size={24} color="#53bdeb" />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '12px' }}>{invoiceForm.invoice_filename || 'Invoice.pdf'}</div>
                          <div style={{ fontSize: '11px', color: '#8696a0' }}>142 KB • PDF Document</div>
                        </div>
                      </div>
                      <div>Hello {invoiceForm.customer_name || 'Priya'}, thank you for your payment!</div>
                      <div style={{ marginTop: '6px' }}>
                        Your invoice for Order <strong>#{invoiceForm.order_id || 'INV-2026-089'}</strong> is attached above.<br />
                        • Amount Paid: <strong>{invoiceForm.amount_formatted || '₹14,999.00'}</strong><br />
                        • Date: {invoiceForm.invoice_date || '05 Mar 2026'}
                      </div>
                      <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '8px', marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#53bdeb', fontWeight: 600, fontSize: '12px' }}>
                          <ExternalLink size={13} /> Track Order Status
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Case 1: Daily Report */}
                  {workflowType === 'report' && (
                    <div>
                      <div style={{ fontWeight: 700, marginBottom: '6px' }}>
                        📊 Daily Business Summary — {reportForm.report_date}
                      </div>
                      <div>Hello {reportForm.merchant_name}, here is your store performance:</div>
                      <div style={{ margin: '8px 0', background: 'rgba(0,0,0,0.15)', borderRadius: '6px', padding: '8px' }}>
                        • Total Revenue: <strong>{reportForm.revenue_str}</strong><br />
                        • Total Orders: <strong>{reportForm.orders_count} completed</strong><br />
                        • Active Visitors: <strong>{reportForm.visitors_count}</strong><br />
                        • Pending Shipments: {reportForm.pending_shipments}
                      </div>
                      <div style={{ fontSize: '12px', color: '#c4d1d9' }}>Keep up the great work!</div>
                      <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '8px', marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#53bdeb', fontWeight: 600, fontSize: '12px' }}>
                          <ExternalLink size={13} /> Open Admin Dashboard
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Custom Message */}
                  {workflowType === 'custom' && (
                    <div>{customForm.message || 'Direct message text...'}</div>
                  )}

                  {/* Timestamp */}
                  <div style={{ textAlign: 'right', fontSize: '10px', color: '#8696a0', marginTop: '4px' }}>
                    Just now • ✓✓
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: DELIVERY LOGS */}
        {activeTab === 'logs' && (
          <div style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '24px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px' }}>Recent WhatsApp Delivery Audit Trail</h2>
            {logsData.logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No message logs recorded yet.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8', textAlign: 'left' }}>
                      <th style={{ padding: '10px' }}>Recipient (E.164)</th>
                      <th style={{ padding: '10px' }}>Workflow</th>
                      <th style={{ padding: '10px' }}>Status</th>
                      <th style={{ padding: '10px' }}>Cost</th>
                      <th style={{ padding: '10px' }}>Message ID</th>
                      <th style={{ padding: '10px' }}>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsData.logs.map((log, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: '12px 10px', fontWeight: 600, color: '#f8fafc' }}>+{log.recipient}</td>
                        <td style={{ padding: '12px 10px' }}>
                          <span style={{ background: 'rgba(255,255,255,0.06)', padding: '3px 8px', borderRadius: '6px', fontSize: '11px' }}>
                            {log.workflow_type}
                          </span>
                        </td>
                        <td style={{ padding: '12px 10px' }}>
                          <span
                            style={{
                              padding: '3px 8px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: 600,
                              background: log.status === 'read' ? 'rgba(34, 197, 94, 0.2)' : log.status === 'delivered' ? 'rgba(56, 189, 248, 0.2)' : log.status === 'failed' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(234, 179, 8, 0.2)',
                              color: log.status === 'read' ? '#4ade80' : log.status === 'delivered' ? '#38bdf8' : log.status === 'failed' ? '#f87171' : '#fde047',
                            }}
                          >
                            {log.status.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '12px 10px', color: '#a855f7', fontWeight: 500 }}>₹{log.cost_inr.toFixed(2)}</td>
                        <td style={{ padding: '12px 10px', color: '#64748b', fontSize: '11px', fontFamily: 'monospace' }}>
                          {log.id ? `${log.id.slice(0, 16)}...` : '-'}
                        </td>
                        <td style={{ padding: '12px 10px', color: '#94a3b8' }}>{new Date(log.timestamp).toLocaleTimeString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: CONFIGURATION */}
        {activeTab === 'config' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(350px, 1fr) minmax(350px, 1fr)', gap: '24px' }}>
            <form onSubmit={handleSaveConfig} style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '24px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Key size={18} color="#25D366" /> Meta API Credentials
              </h2>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#cbd5e1', marginBottom: '4px' }}>WhatsApp Phone Number ID</label>
                <input
                  type="text"
                  placeholder="e.g. 109823485728392"
                  value={configForm.phone_number_id}
                  onChange={(e) => setConfigForm({ ...configForm, phone_number_id: e.target.value })}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#cbd5e1', marginBottom: '4px' }}>WhatsApp Business Account ID (WABA)</label>
                <input
                  type="text"
                  placeholder="e.g. 192837465019283"
                  value={configForm.waba_id}
                  onChange={(e) => setConfigForm({ ...configForm, waba_id: e.target.value })}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#cbd5e1', marginBottom: '4px' }}>
                  Meta System User Access Token {config.has_access_token && <span style={{ color: '#22c55e' }}>(Saved: {config.masked_token})</span>}
                </label>
                <input
                  type="password"
                  placeholder="Enter new token to update"
                  value={configForm.access_token}
                  onChange={(e) => setConfigForm({ ...configForm, access_token: e.target.value })}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#cbd5e1', marginBottom: '4px' }}>Default Country Code</label>
                <input
                  type="text"
                  placeholder="91"
                  value={configForm.default_country_code}
                  onChange={(e) => setConfigForm({ ...configForm, default_country_code: e.target.value })}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  background: '#25D366',
                  color: '#042f1a',
                  fontWeight: 600,
                  fontSize: '14px',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  cursor: 'pointer',
                  marginTop: '10px',
                }}
              >
                Save Meta Configuration
              </button>
            </form>

            {/* Webhook Info Card */}
            <div style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '24px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Zap size={18} color="#38bdf8" /> Webhook Setup (Meta Console)
              </h2>
              <p style={{ fontSize: '13px', color: '#94a3b8', lineHeight: 1.5, margin: '0 0 16px' }}>
                Configure these webhook credentials in your Meta for Developers App under WhatsApp &gt; Configuration:
              </p>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#cbd5e1', marginBottom: '4px' }}>Callback URL</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    readOnly
                    value={`${API_URL}/api/v1/whatsapp/webhook`}
                    style={{ flex: 1, background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#38bdf8', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                  />
                  <button
                    onClick={() => copyToClipboard(`${API_URL}/api/v1/whatsapp/webhook`, 'webhook_url')}
                    style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '8px', color: '#fff', padding: '0 12px', cursor: 'pointer' }}
                  >
                    {copiedKey === 'webhook_url' ? <Check size={16} color="#22c55e" /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#cbd5e1', marginBottom: '4px' }}>Verify Token</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    readOnly
                    value={config.webhook_verify_token}
                    style={{ flex: 1, background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', color: '#38bdf8', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                  />
                  <button
                    onClick={() => copyToClipboard(config.webhook_verify_token, 'verify_token')}
                    style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '8px', color: '#fff', padding: '0 12px', cursor: 'pointer' }}
                  >
                    {copiedKey === 'verify_token' ? <Check size={16} color="#22c55e" /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
              <div style={{ background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.2)', borderRadius: '8px', padding: '12px', fontSize: '12px', color: '#bae6fd' }}>
                💡 <strong>Webhook Subscriptions:</strong> Subscribe to <code>messages</code> in Meta Console to receive Sent, Delivered, and Read delivery receipts in real time.
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: R&D GUIDE */}
        {activeTab === 'guide' && (
          <div style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '24px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px' }}>Meta WhatsApp Business Cloud API — R&D Rules & Cost Summary</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: '#0f172a', borderRadius: '12px', padding: '16px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontWeight: 600, color: '#38bdf8', marginBottom: '6px' }}>🔐 Authentication (OTP)</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#fff', marginBottom: '6px' }}>~₹0.12 - ₹0.15</div>
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>Per delivered OTP message. Includes native 1-tap Copy Code button.</p>
              </div>
              <div style={{ background: '#0f172a', borderRadius: '12px', padding: '16px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontWeight: 600, color: '#4ade80', marginBottom: '6px' }}>📄 Utility (Invoices & Reports)</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#fff', marginBottom: '6px' }}>~₹0.11 - ₹0.14</div>
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>Per 24-hr utility conversation. Supports PDF document attachments & URL CTAs.</p>
              </div>
              <div style={{ background: '#0f172a', borderRadius: '12px', padding: '16px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontWeight: 600, color: '#fde047', marginBottom: '6px' }}>💬 Service (Inbound Support)</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#fff', marginBottom: '6px' }}>FREE (1,000/mo)</div>
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>Customer-initiated support conversations are 100% free for first 1k chats/mo.</p>
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: '1.6' }}>
              <h3 style={{ fontSize: '15px', color: '#fff', marginTop: '16px' }}>Key Phone Number Rules:</h3>
              <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                <li>Any mobile SIM, landline, or VoIP number can be used.</li>
                <li>The number must not be currently active on the regular consumer WhatsApp mobile app.</li>
                <li>Messages are sent directly from Meta Cloud servers with 0% middleman markup.</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppDashboard;
